import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import baseService from "@/services/baseService";
import { missionService } from "@/services/missionService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import "leaflet/dist/leaflet.css";
import { useEffect, useRef, useState } from "react";
import { MapContainer, Marker, Polygon, Popup, TileLayer, useMap, useMapEvents, } from "react-leaflet";
import { useNavigate } from "react-router-dom";
// import { area } from "@turf/area";
// Fix Leaflet icon issue
import L from "leaflet";
import { toast, Toaster } from "sonner";
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});
// Custom icon for bases
const baseIcon = new L.Icon({
    iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});
function MapClickHandler({ onMapClick, }) {
    useMapEvents({
        click: (e) => {
            onMapClick(e.latlng);
        },
    });
    return null;
}
function MapCenter({ newLocation }) {
    const map = useMap();
    useEffect(() => {
        if (newLocation)
            map.flyTo([newLocation.lat, newLocation.lon], 13);
    }, [newLocation, map]);
    return null;
}
export default function MissionPlanner() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [polygonPoints, setPolygonPoints] = useState([]);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        site_name: "",
        survey_type: "mapping",
        altitude: 50,
        speed: 10,
        overlap: 70,
    });
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [suggestion, setSuggestion] = useState([]);
    const [showDropDown, setShowDropDown] = useState(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
    const [newLocation, setNewLocation] = useState(null);
    const listRef = useRef([]);
    // const [polygonDist, setPolygonDist] = useState<number[]>([]);
    // const polygonArea = useState(null);
    // Fetch all bases
    const { data: basesData } = useQuery({
        queryKey: ["bases"],
        queryFn: () => baseService.getAll(),
    });
    const bases = basesData?.data || [];
    const createMissionMutation = useMutation({
        mutationFn: (data) => missionService.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["missions"] });
            setPolygonPoints([]);
            setFormData({
                name: "",
                description: "",
                site_name: "",
                survey_type: "mapping",
                altitude: 50,
                speed: 10,
                overlap: 70,
            });
            // Redirect to missions page after successful creation
            navigate("/missions");
        },
    });
    const handleMapClick = (point) => {
        const lastPoint = polygonPoints[polygonPoints.length - 1];
        if (lastPoint) {
            const distance = lastPoint.distanceTo(point);
            if (distance > 5000) {
                toast.error("Distance between the points exceeds 5000 meters. Please select a closer point.");
                return;
            }
            // setPolygonDist((prevDistances) => [...prevDistances, distance]);
        }
        const updatedPoints = [...polygonPoints, point];
        if (updatedPoints.length >= 5) {
            const avg_lat = updatedPoints.reduce((sum, currentPoint) => {
                return sum + currentPoint.lat;
            }, 0) / updatedPoints.length;
            const lat_to_meters = 111000;
            const lon_to_meters = 111000 * Math.cos((avg_lat * Math.PI) / 180);
            const coordinates_in_meters = updatedPoints.map((p) => ({
                x: p.lng * lon_to_meters,
                y: p.lat * lat_to_meters
            }));
            let polygonArea = 0;
            for (let i = 0; i < coordinates_in_meters.length; i++) {
                const next_idx = (i + 1) % coordinates_in_meters.length;
                polygonArea += coordinates_in_meters[i].x * coordinates_in_meters[next_idx].y;
                polygonArea -= coordinates_in_meters[next_idx].x * coordinates_in_meters[i].y;
            }
            polygonArea = Math.abs(polygonArea / 2);
            if (polygonArea > 100000) {
                toast.error("The area of the polygon exceeds 100,000 square meters. Please select points that form a smaller area.");
                return;
            }
        }
        setPolygonPoints(updatedPoints);
    };
    const handleClearLastPolygonPoint = () => {
        setPolygonPoints((previousPoints) => previousPoints.slice(0, -1));
    };
    const handleClearPolygon = () => {
        setPolygonPoints([]);
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name || !formData.site_name) {
            alert("Please fill in mission name and site name");
            return;
        }
        if (polygonPoints.length < 3) {
            alert("Please draw a survey area (at least 3 points)");
            return;
        }
        const coverage_area = {
            type: "Polygon",
            coordinates: [
                [
                    ...polygonPoints.map((p) => [p.lng, p.lat]),
                    [polygonPoints[0].lng, polygonPoints[0].lat],
                ],
            ],
        };
        createMissionMutation.mutate({
            ...formData,
            coverage_area,
        });
    };
    useEffect(() => {
        if (!search.trim()) {
            setSuggestion([]);
            setShowDropDown(false);
            setLoading(false);
            return;
        }
        const controller = new AbortController();
        const { signal } = controller;
        const fetchData = async () => {
            setShowDropDown(true);
            setLoading(true);
            try {
                const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${search}&format=jsonv2`, { signal });
                const data = await response.json();
                setSuggestion(data);
            }
            catch (error) {
                if (error !== "AbortError")
                    console.error("Error fetching search results:", error);
            }
            finally {
                setLoading(false);
            }
        };
        fetchData();
        return () => {
            controller.abort();
        };
    }, [search]);
    useEffect(() => {
        const selectedItem = listRef.current[selectedSuggestionIndex];
        selectedItem?.scrollIntoView({
            block: "nearest",
            behavior: "smooth",
        });
    }, [selectedSuggestionIndex]);
    const handleSuggestionClick = (item) => {
        setNewLocation({ lat: item.lat, lon: item.lon });
        setShowDropDown(false);
    };
    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedSuggestionIndex((prevIndex) => Math.min(prevIndex + 1, suggestion.length - 1));
        }
        else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedSuggestionIndex((prevIndex) => Math.max(prevIndex - 1, 0));
        }
        else if (e.key === "Enter") {
            e.preventDefault();
            if (suggestion[selectedSuggestionIndex]) {
                handleSuggestionClick(suggestion[selectedSuggestionIndex]);
                setSuggestion([]);
                setShowDropDown(false);
            }
        }
    };
    return (_jsxs("div", { className: "p-8", children: [_jsxs("div", { className: "mb-8", children: [_jsx("h1", { className: "text-3xl font-bold text-foreground", children: "Mission Planner" }), _jsx("p", { className: "mt-2 text-muted-foreground", children: "Plan and configure drone tasks" })] }), _jsx(Toaster, { position: "top-right" }), _jsxs("div", { className: "grid grid-cols-1 lg:grid-cols-3 gap-6", children: [_jsx("div", { className: "lg:col-span-2", children: _jsxs("div", { className: "bg-card rounded-lg border shadow-sm overflow-hidden", children: [_jsxs("div", { className: "px-6 py-4 border-b flex items-center justify-between", children: [_jsx("h2", { className: "text-[17px] font-semibold text-card-foreground", children: "Map out Survey Area (Select atleast 3 points in the map)" }), polygonPoints.length > 0 ? (_jsxs("div", { className: "flex gap-2", children: [_jsxs("button", { onClick: handleClearPolygon, className: "text-[12px] text-muted-foreground hover:text-foreground px-3 py-1 rounded border", children: ["Clear (", polygonPoints.length, " points)"] }), _jsx("button", { onClick: handleClearLastPolygonPoint, disabled: polygonPoints.length === 1, className: "text-[12px] text-muted-foreground hover:text-foreground px-3 py-1 rounded border disabled:cursor-not-allowed", children: "Clear previous point" })] })) : (_jsxs("div", { className: "w-1/3 relative", children: [_jsx("input", { name: "Search Input", className: "text-[12px] w-full px-3 py-1 bg-background border rounded-md text-foreground", type: "text", value: search, onChange: (e) => setSearch(e.target.value), onKeyDown: handleKeyDown, placeholder: "Enter city, address, or country..." }), showDropDown &&
                                                    (loading || suggestion.length > 0) && (_jsx("ul", { className: "suggestion-dropdown w-full absolute bg-white rounded-md list-none p-0 m-0 max-h-[200px] overflow-y-auto z-[1000] shadow-[0_4px_6px_rgba(0,0,0,0.1)]", children: loading ? (_jsx("li", { className: "px-2 py-1", children: "Loading..." })) : (suggestion.map((item, index) => (_jsx("li", { ref: (element) => {
                                                            listRef.current[index] = element;
                                                        }, className: `suggestion-item cursor-pointer border-b px-2 py-1 hover:bg-gray-300 ${index === selectedSuggestionIndex && "bg-gray-300"}`, onClick: () => setSelectedSuggestionIndex(index), onMouseDown: () => handleSuggestionClick(item), children: item.display_name }, `${item.lat}-${item.lon}`)))) }))] }))] }), _jsx("div", { className: "h-[613px]", children: _jsxs(MapContainer, { center: [20.5937, 78.9629], zoom: 5, style: { height: "100%", width: "100%" }, children: [_jsx(TileLayer, { url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png?app=MyReactAppName&contact=prateek0426@gmail.com", attribution: '\u00A9 <a href="https://openstreetmap.org">OpenStreetMap</a> contributors', keepBuffer: 2, updateWhenIdle: true }), _jsx(MapClickHandler, { onMapClick: handleMapClick }), _jsx(MapCenter, { newLocation: newLocation }), bases.map((base) => (_jsx(Marker, { position: [base.lat, base.lng], icon: baseIcon, children: _jsx(Popup, { autoClose: false, children: _jsxs("div", { className: "p-2", children: [_jsx("h3", { className: "font-semibold text-sm mb-1", children: base.name }), _jsxs("div", { className: "text-xs space-y-1", children: [_jsxs("div", { className: "flex justify-between", children: [_jsx("span", { className: "text-muted-foreground", children: "Status:" }), _jsx("span", { className: "capitalize font-medium", children: base.status })] }), _jsxs("div", { className: "flex justify-between", children: [_jsx("span", { className: "text-muted-foreground", children: "Capacity:" }), _jsxs("span", { children: [base.capacity, " ", "drones"] })] }), _jsxs("div", { className: "flex justify-between", children: [_jsx("span", { className: "text-muted-foreground", children: "Drones:" }), _jsx("span", { children: base.drone_count ||
                                                                                    0 })] }), _jsxs("div", { className: "text-xs text-muted-foreground mt-1", children: [base.lat.toFixed(4), ",", " ", base.lng.toFixed(4)] })] })] }) }) }, base.base_id))), polygonPoints.map((point, idx) => (_jsx(Marker, { position: point }, idx))), polygonPoints.length >= 3 && (_jsx(Polygon, { positions: polygonPoints, pathOptions: {
                                                    color: "blue",
                                                    fillOpacity: 0.2,
                                                } }))] }) })] }) }), _jsx("div", { className: "lg:col-span-1", children: _jsxs("form", { onSubmit: handleSubmit, className: "bg-card rounded-lg border shadow-sm p-6", children: [_jsx("h2", { className: "text-lg font-semibold text-card-foreground mb-4", children: "Mission Configuration" }), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-foreground mb-1", children: "Mission Name *" }), _jsx("input", { type: "text", value: formData.name, onChange: (e) => setFormData({
                                                        ...formData,
                                                        name: e.target.value,
                                                    }), className: "w-full px-3 py-2 bg-background border rounded-md text-foreground", placeholder: "e.g., Site Survey #1", required: true })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-foreground mb-1", children: "Site Name *" }), _jsx("input", { type: "text", value: formData.site_name, onChange: (e) => setFormData({
                                                        ...formData,
                                                        site_name: e.target.value,
                                                    }), className: "w-full px-3 py-2 bg-background border rounded-md text-foreground", placeholder: "e.g., Construction Site A", required: true })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-foreground mb-1", children: "Description" }), _jsx("textarea", { value: formData.description, onChange: (e) => setFormData({
                                                        ...formData,
                                                        description: e.target.value,
                                                    }), className: "w-full px-3 py-2 bg-background border rounded-md text-foreground", rows: 3, placeholder: "Optional mission details" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-foreground mb-1", children: "Survey Type" }), _jsxs("select", { value: formData.survey_type, onChange: (e) => setFormData({
                                                        ...formData,
                                                        survey_type: e.target.value,
                                                    }), className: "w-full px-3 py-2 bg-background border rounded-md text-foreground", children: [_jsx("option", { value: "mapping", children: "Mapping" }), _jsx("option", { value: "inspection", children: "Inspection" }), _jsx("option", { value: "surveillance", children: "Surveillance" }), _jsx("option", { value: "delivery", children: "Delivery" })] })] }), _jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-foreground mb-1", children: "Altitude (m)" }), _jsx("input", { type: "number", value: formData.altitude, onChange: (e) => setFormData({
                                                                ...formData,
                                                                altitude: Number(e.target.value),
                                                            }), className: "w-full px-3 py-2 bg-background border rounded-md text-foreground", min: "10", max: "120" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-foreground mb-1", children: "Speed (m/s)" }), _jsx("input", { type: "number", value: formData.speed, onChange: (e) => setFormData({
                                                                ...formData,
                                                                speed: Number(e.target.value),
                                                            }), className: "w-full px-3 py-2 bg-background border rounded-md text-foreground", min: "1", max: "20" })] })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-foreground mb-1", children: "Overlap (%)" }), _jsx("input", { type: "number", value: formData.overlap, onChange: (e) => setFormData({
                                                        ...formData,
                                                        overlap: Number(e.target.value),
                                                    }), className: "w-full px-3 py-2 bg-background border rounded-md text-foreground", min: "0", max: "90" })] }), _jsx("button", { type: "submit", disabled: createMissionMutation.isPending ||
                                                polygonPoints.length < 3, className: "w-full py-2 px-4 bg-foreground text-background rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium", children: createMissionMutation.isPending
                                                ? "Creating..."
                                                : "Create Mission" }), createMissionMutation.isSuccess && (_jsx("p", { className: "text-sm text-muted-foreground text-center", children: "Mission created successfully!" })), createMissionMutation.isError && (_jsx("p", { className: "text-sm text-muted-foreground text-center", children: "Error creating mission" }))] })] }) })] })] }));
}
